import { useMemo } from 'react';
import type { DropdownMenuItem } from '@frekuent/orbit/components';
import { ControlledSelect } from '@/legacy/components/ui/Form/Select/recipes/ControlledSelect';
import SettingsIcon from '@/legacy/components/ui/Icons/SettingsIcon';
import { LogoutIcon } from '@/legacy/components/ui/Icons';
import { NotificationIcon } from '@/shared/ui/icons';
import UserDropdownIcon from '@/shared/ui/icons/UserDropdownIcon';
import { paths } from '@/app/router/path';
import LanguageOptionLabel from '../components/LanguageOptionLabel';
import { useTopBar } from './useTopBar';

export type TopBarMenuItemsInput = Pick<
	ReturnType<typeof useTopBar>,
	| 't'
	| 'navigate'
	| 'unreadCount'
	| 'hasSettingsAccess'
	| 'openOffcanvas'
	| 'openLogoutModal'
	| 'profile'
	| 'isLoading'
	| 'isPendingSaveProfile'
	| 'onSubmit'
	| 'languageOptions'
>;

export const useTopBarMenuItems = ({
	t,
	navigate,
	unreadCount,
	hasSettingsAccess,
	openOffcanvas,
	openLogoutModal,
	profile,
	isLoading,
	isPendingSaveProfile,
	onSubmit,
	languageOptions,
}: TopBarMenuItemsInput) => {
	const headerItems: DropdownMenuItem[] = useMemo(
		() => [
			{
				id: 'profile',
				label: t('topbar.profile'),
				onSelect: () => navigate('/app/profile'),
				icon: (
					<UserDropdownIcon
						width={16}
						height={16}
					/>
				),
			},
			// Settigs comes back provisionally to TopBar after removing it from
			// the CompanySwitchModal, in case it is not clear how to get there
			...(hasSettingsAccess
				? [
						{
							id: 'settings',
							label: t('topbar.settings'),
							onSelect: () => navigate(paths.settings.main()),
							icon: (
								<SettingsIcon
									width={16}
									height={16}
								/>
							),
						},
					]
				: []),
		],
		[t, navigate, hasSettingsAccess],
	);

	const tailItems: DropdownMenuItem[] = useMemo(
		() => [
			{
				type: 'custom',
				id: 'language',
				content: (
					<div className='px-3 py-2 hover:bg-secondary-ultra-light'>
						<ControlledSelect
							options={languageOptions}
							value={profile?.language ?? ''}
							onChange={onSubmit}
							name='locale'
							isLoadingOptions={isLoading || isPendingSaveProfile}
							spacing={false}
							ignoreSize={true}
							variant='menu-item'
							displayValue={<LanguageOptionLabel />}
						/>
					</div>
				),
			},
			{
				id: 'logout',
				label: t('topbar.logout'),
				onSelect: openLogoutModal,
				icon: (
					<LogoutIcon
						width={16}
						height={16}
					/>
				),
			},
		],
		[
			t,
			languageOptions,
			profile?.language,
			onSubmit,
			isLoading,
			isPendingSaveProfile,
			openLogoutModal,
		],
	);

	const mobileMenuItems: DropdownMenuItem[] = useMemo(
		() => [
			...headerItems,
			{
				id: 'notifications',
				label: `${t('topbar.notifications')} (${unreadCount})`,
				onSelect: () => openOffcanvas('notifications'),
				icon: (
					<NotificationIcon
						width={16}
						height={16}
					/>
				),
			},
			{ type: 'separator', id: 'separator' },
			...tailItems,
		],
		[t, unreadCount, openOffcanvas, headerItems, tailItems],
	);

	const desktopMenuItems: DropdownMenuItem[] = useMemo(
		() => [...headerItems, { type: 'separator', id: 'separator' }, ...tailItems],
		[headerItems, tailItems],
	);

	return { mobileMenuItems, desktopMenuItems };
};
